import {
    Box,
    Button,
    Flex,
    Grid,
    Heading,
    Skeleton,
    Text,
} from '@radix-ui/themes';

import { PaginationControls } from '@common/types/pagination';
import { QueryResponse } from '@common/types/queryState';
import { CaretRightIcon } from '@radix-ui/react-icons';

import { Trans } from 'react-i18next';
import styles from './Avenue.module.css';
import { AvenuePaginationControl } from './AvenuePaginationControl';
export interface AvenueItem {
    id: string;
}

export interface AvenueProps<T extends AvenueItem>
    extends QueryResponse<T>,
        PaginationControls {
    title: string;
    isLoading: boolean;

    renderItem: (item: T) => React.ReactNode;
    onSeeAll: () => void;
}

export const Avenue = <T extends AvenueItem>({
    title,
    data,
    totalCount,
    renderItem,
    onSeeAll,
    isLoading,
    ...pagination
}: AvenueProps<T>) => {
    const hasMoreItems = data.length < totalCount;

    return (
        <Box className={styles.avenueWrapper}>
            <Button
                className={styles.avenueButton}
                variant={'ghost'}
                color={'gray'}
                onClick={onSeeAll}
                ml={'2'}
            >
                <Flex gap={'2'} align={'baseline'}>
                    <Heading highContrast size={'6'} weight={'bold'}>
                        {title}
                    </Heading>

                    <Text size={'5'}>({totalCount})</Text>

                    {hasMoreItems && (
                        <Flex ml={'2'} align={'center'}>
                            <Text size={'3'}>
                                <Trans
                                    i18nKey={'common:pagination.seeMore_one'}
                                />
                            </Text>

                            <CaretRightIcon width={'24'} height={'24'} />
                        </Flex>
                    )}
                </Flex>
            </Button>

            <Box position={'relative'}>
                <AvenuePaginationControl {...pagination} />

                <Grid
                    columns={`${pagination.pageSize}`}
                    gap={'4'}
                    m={'2'}
                    ml={pagination.hasPreviousPage ? '4' : '2'}
                    mr={pagination.hasNextPage ? '4' : '2'}
                >
                    {data.map((item) => (
                        <Skeleton key={item.id} loading={isLoading}>
                            <Box
                                style={{
                                    flexShrink: 0,
                                    scrollSnapAlign: 'start',
                                }}
                            >
                                {renderItem(item)}
                            </Box>
                        </Skeleton>
                    ))}
                </Grid>
            </Box>
        </Box>
    );
};
